<script setup lang="ts">
/**
 * Functionally the grid layout is pretty simple, it's just a series of grid items which each have
 * a dynamic slot name (based on the layout item key) then you just define a list of bindings that
 * bind to each one uniquely, and this way it'll then bind the slots nicely.
 *
 * This avoids useless configuration and knowledge of grid items.
 */

import { useResizeObserver } from '@Shared/utils/useResizeObserver';
import { computed, ref } from 'vue';
import GridItem from './GridItem.vue';
import {
  CompactType,
  LayoutDetails,
  LayoutItem,
  LayoutItemOffset,
  ResponsiveMap,
} from '@Shared/components/GridLayout/types';
import { findHeightOfLayout } from './utils/layoutUtils';
import { getBreakpointFromWidth, lookupBreakpoint } from './utils/responsiveUtils';

interface GridLayoutProps<T extends { [key: string]: number }> {
  modelValue: ReadonlyArray<LayoutItem> | ResponsiveMap<T, ReadonlyArray<LayoutItem>>;
  breakpoints: T;
  cols: number | ResponsiveMap<T, number>;
  margin: LayoutItemOffset | ResponsiveMap<T, LayoutItemOffset>;
  containerPadding: LayoutItemOffset | ResponsiveMap<T, LayoutItemOffset>;
  useCssTransforms: boolean;
  compactRules: CompactType;
  rowHeight: number;
}

// FIXME: Vue3 doesn't support generics so we can't validate this easily here
// When the RFC is accepted we can most likely implement a patch here to use it
// as specified here: https://github.com/vuejs/rfcs/discussions/436
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const props = defineProps<GridLayoutProps<any>>();

const root = ref<HTMLElement | null>(null);
const containerSize = useResizeObserver(root);

const breakpoint = computed<string>(() => {
  return getBreakpointFromWidth(props.breakpoints, containerSize.value?.width || 0);
});

const responsiveLayout = computed<ReadonlyArray<LayoutItem>>(() =>
  lookupBreakpoint<ReadonlyArray<LayoutItem>>(breakpoint.value, props.modelValue)
);

const responsiveCols = computed<number>(() => lookupBreakpoint<number>(breakpoint.value, props.cols));

const responsiveContainerPadding = computed<LayoutItemOffset>(() =>
  lookupBreakpoint<LayoutItemOffset>(breakpoint.value, props.containerPadding)
);

const responsiveMargin = computed<LayoutItemOffset>(() =>
  lookupBreakpoint<LayoutItemOffset>(breakpoint.value, props.margin)
);

const responsiveColWidth = computed<number>(() => {
  return (
    ((containerSize.value?.width ?? 0) -
      responsiveMargin.value.x * (responsiveCols.value - 1) -
      responsiveContainerPadding.value.x * 2) /
    responsiveCols.value
  );
});

const details = computed<LayoutDetails>(() => {
  return {
    rowHeight: props.rowHeight,
    cols: responsiveCols.value,
    columnWidth: responsiveColWidth.value,
    containerWidth: containerSize.value?.width ?? 0,
    containerPadding: responsiveContainerPadding.value,
    margin: responsiveMargin.value,
  };
});

const spacerConfig = computed<LayoutItem>(() => {
  const height = findHeightOfLayout(responsiveLayout.value);

  return {
    key: '$__internalSpacer',
    x: 0,
    y: height,
    w: responsiveCols.value,
    h: 1,
  } as LayoutItem;
});
</script>
<template>
  <div ref="root" class="grid-layout w-full h-full">
    <grid-item
      v-for="item in responsiveLayout"
      :key="item.key"
      :item="item"
      :use-css-transforms="useCssTransforms"
      :details="details"
    >
      <slot :name="item.key"></slot>
    </grid-item>
    <grid-item
      class="spacer"
      :item="spacerConfig"
      :use-css-transforms="useCssTransforms"
      :details="details"
      :style="{
        height: `${responsiveContainerPadding.y - responsiveMargin.y}px`,
        background: 'none',
        border: 'none',
      }"
    >
      <slot name="$__internalSpacer"></slot>
    </grid-item>
  </div>
</template>
<style>
.grid-layout {
  position: relative;
  transition: height 200ms ease;
}
</style>
